import React from "react";
import {createPortal} from "react-dom";
import {RedoOutlined, FormOutlined, InboxOutlined, NumberOutlined} from "@ant-design/icons";
import "./ZoneContextMenu.css";

/**
 * Menu contestuale gabbia (pre-elaborazione): azione AI + campi manuali.
 * UI custom, posizionata al cursore con clamping nel viewport. La chiusura on-click-outside
 * è gestita dalla pagina (listener che ignora i click dentro `.zone-ctx-menu`).
 */
export default function ZoneContextMenu({
	x,
	y,
	targetCount = 1,
	// Conteggio SEPARATO per l'etichetta "Rielabora selezionate (N)": la rielaborazione AI
	// riguarda solo le gabbie, mai i banner, quindi su una selezione mista (gabbie + banner)
	// il numero può essere minore di targetCount (che è il bersaglio di "Imposta campi
	// manuali", filtrato per il tipo su cui è caduto il tasto destro). Di default coincide
	// con targetCount per chi non lo passa esplicitamente.
	reprocessCount = targetCount,
	fields = [],
	onReprocess,
	reprocessDisabled = false,
	onOpenFields,
	// Rinumerazione manuale: il chiamante la passa solo se ha senso (una sola gabbia, non
	// ancora elaborata, e nelle varianti solo se "nuova"); `editSerialDisabledReason` la mostra
	// spenta con il motivo invece di nasconderla, così l'operatore capisce perché non può.
	onEditSerial,
	editSerialDisabledReason = "",
	onClose
})
{
	const ref = React.useRef(null);
	const [pos, setPos] = React.useState({left: x, top: y, ready: false});

	React.useLayoutEffect(() =>
	{
		const el = ref.current;
		if (!el)
			return;

		const rect = el.getBoundingClientRect();
		const m = 8;
		let left = x;
		let top = y;

		if (left + rect.width + m > window.innerWidth)
			left = x - rect.width;
		if (top + rect.height + m > window.innerHeight)
			top = window.innerHeight - rect.height - m;

		setPos({left: Math.max(m, left), top: Math.max(m, top), ready: true});
	}, [x, y, fields.length]);

	const title = targetCount > 1 ? `${targetCount} gabbie selezionate` : "Gabbia selezionata";

	const menu = (
		<div
			ref={ref}
			className="zone-ctx-menu"
			style={{left: pos.left, top: pos.top, visibility: pos.ready ? "visible" : "hidden"}}
			onContextMenu={(e) => e.preventDefault()}
		>
			<div className="zone-ctx-title">{title}</div>

			{onReprocess ? (
				<>
					<button
						type="button"
						className="zone-ctx-row zone-ctx-row--ai"
						disabled={reprocessDisabled}
						onClick={() =>
						{
							if (reprocessDisabled)
								return;
							onReprocess();
							onClose?.();
						}}
					>
						<span className="zone-ctx-ico zone-ctx-ico--ai"><RedoOutlined/></span>
						<span className="zone-ctx-label">
							{reprocessCount > 1 ? `Rielabora selezionate (${reprocessCount})` : "Rielabora con AI"}
						</span>
					</button>
					{fields.length > 0 ? <div className="zone-ctx-sep"/> : null}
				</>
			) : null}

			{onEditSerial ? (
				<>
					<button
						type="button"
						className="zone-ctx-row zone-ctx-row--fields"
						disabled={!!editSerialDisabledReason}
						title={editSerialDisabledReason || undefined}
						onClick={() =>
						{
							if (editSerialDisabledReason)
								return;
							onEditSerial();
							onClose?.();
						}}
					>
						<span className="zone-ctx-ico zone-ctx-ico--fields"><NumberOutlined/></span>
						<span className="zone-ctx-label">Modifica seriale</span>
					</button>
					{fields.length > 0 ? <div className="zone-ctx-sep"/> : null}
				</>
			) : null}

			{fields.length > 0 ? (
				<button
					type="button"
					className="zone-ctx-row zone-ctx-row--fields"
					onClick={() =>
					{
						onOpenFields?.();
						onClose?.();
					}}
				>
					<span className="zone-ctx-ico zone-ctx-ico--fields"><FormOutlined/></span>
					<span className="zone-ctx-label">Imposta campi manuali</span>
				</button>
			) : !onReprocess && !onEditSerial ? (
				<div className="zone-ctx-empty">
					<InboxOutlined/>
					<span>Nessun campo manuale disponibile</span>
				</div>
			) : null}
		</div>
	);

	return createPortal(menu, document.body);
}
